import cn from 'classnames';
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  ReactNode,
  useId,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Checkbox, Icon } from '@/shared/ui';

import type { TemplateIssue } from '@/entities/templates';

import { useTemplateIssueText } from '../hooks';
import {
  getNoteChoiceSelection,
  isNoteSlotId,
  isTemplateWarning,
  noteAllowsSetting,
  setNoteBlockMultiple,
  updateNoteChoice,
  type NoteBlockDef,
} from '../lib';

import styles from './FieldSettings.module.scss';
import {
  ModeSwitch,
  NoneSettings,
  SettingsField,
  TableSettings,
  UnitSelect,
} from './ValueTypeSettings';

// Scribe's own words, in English, stay at hand for support.
export const TemplateIssues: FC<{
  issues: TemplateIssue[];
  className?: string;
}> = ({ issues, className }) => {
  const { describe } = useTemplateIssueText();

  return issues.length ? (
    <ul
      role="alert"
      className={cn(styles.issues, className)}
      data-component="workspace-template-issues"
    >
      {issues.map((issue, index) => (
        <li
          key={`${issue.Kind}/${index}`}
          className={cn(
            'p4',
            styles.issue,
            isTemplateWarning(issue) && styles.issueWarning,
          )}
          title={issue.Message || undefined}
        >
          {describe(issue)}
        </li>
      ))}
    </ul>
  ) : null;
};

// A start that is the explicit none goes when the block no longer takes it.
const dropNoneDefault = (block: NoteBlockDef): NoteBlockDef =>
  (block.Kind.value as { AllowNone?: boolean } | undefined)?.AllowNone ||
  !block.Default?.None
    ? block
    : { ...block, Default: undefined };

// The template editor's pop-ups: a few settings of a field, an instruction
// or a section, above it.

// The id a field or section is stored under: read only, and copied by its
// button.
const IdRow: FC<{ id: string }> = ({ id }) => {
  const { formatMessage } = useIntl();
  const copyLabel = formatMessage({
    id: 'global.copy',
    defaultMessage: 'Copy',
  });
  return (
    <div className={styles.field} data-component="workspace-template-popup-id">
      <span className={styles.fieldLabel}>
        <FormattedMessage id="workspace.templateEdit.id" defaultMessage="ID" />
      </span>
      <span className={styles.idRow}>
        <code className={cn('p4', styles.idValue)}>{id}</code>
        <button
          type="button"
          className={styles.idCopy}
          aria-label={copyLabel}
          title={copyLabel}
          onClick={() => {
            void navigator.clipboard?.writeText(id).catch(() => {
              /* ignore — the id stays in sight to copy by hand */
            });
          }}
        >
          <Icon name="copy" size={14} />
        </button>
      </span>
    </div>
  );
};

// What a text box holds while it is typed in; it applies on Enter in a
// one-line box or on leaving it, and Escape restores what the template
// holds.
const useCommittedText = (value: string, onCommit: (next: string) => void) => {
  const [text, setText] = useState(value);
  const [held, setHeld] = useState(value);
  if (held !== value) {
    setHeld(value);
    setText(value);
  }
  return {
    value: text,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setText(event.target.value),
    onBlur: () => {
      if (text !== value) {
        onCommit(text);
      }
    },
    onKeyDown: (
      event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setText(value);
      }
      if (event.key === 'Enter' && event.currentTarget.tagName === 'INPUT') {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
  };
};

// A field's code (its slot id): empty unless the author gives one, in
// scribe's format.
const CodeInput: FC<{ value: string; onChange: (code: string) => void }> = ({
  value,
  onChange,
}) => {
  const problemId = useId();
  const box = useCommittedText(value, (next) => onChange(next.trim()));
  const wrong = !!box.value.trim() && !isNoteSlotId(box.value.trim());
  return (
    <SettingsField
      label={<FormattedMessage id="global.code" defaultMessage="Code" />}
    >
      <input
        className={cn('p4', styles.popupCode)}
        aria-invalid={wrong || undefined}
        aria-describedby={wrong ? problemId : undefined}
        data-component="workspace-template-popup-code"
        {...box}
      />
      {wrong && (
        <span id={problemId} className={cn('p4', styles.issue)}>
          <FormattedMessage
            id="workspace.templateEdit.codeFormat"
            defaultMessage="A letter first, then letters, digits and _; parts are joined by dots."
          />
        </span>
      )}
    </SettingsField>
  );
};

const PopupSettings: FC<{ dataComponent: string; children: ReactNode }> = ({
  dataComponent,
  children,
}) => (
  <div
    className={cn(styles.settings, styles.popup)}
    data-component={dataComponent}
  >
    {children}
  </div>
);

const RemoveButton: FC<{ children: ReactNode; onClick: () => void }> = ({
  children,
  onClick,
}) => (
  <Button
    variant="tertiary"
    size="small"
    danger
    icon="delete"
    className={styles.addSlot}
    onClick={onClick}
  >
    {children}
  </Button>
);

type NoneHolder = { AllowNone?: boolean; NoneLabel?: string };

type FieldPopupProps = {
  block: NoteBlockDef;
  onChange: (block: NoteBlockDef) => void;
  onRemove: () => void;
};

// A field's pop-up: its id and code; whether a choice takes several values
// or one of the clinician's own, and a teeth field several teeth; a
// number's unit; a table's columns, without which scribe refuses it; the
// explicit "no" where scribe allows it; and its removal. Every other
// setting, its AI instruction among them, is kept as stored.
export const FieldPopupSettings: FC<FieldPopupProps> = ({
  block,
  onChange,
  onRemove,
}) => {
  const { formatMessage } = useIntl();
  const { Kind } = block;
  const selection = getNoteChoiceSelection(block);
  const choice =
    Kind.case === 'Choice' ||
    Kind.case === 'MultiChoice' ||
    Kind.case === 'ListRef'
      ? Kind
      : null;
  const none = Kind.value as NoneHolder | undefined;
  const setOther = (OtherAllowed: boolean) => {
    if (!choice) {
      return;
    }
    onChange(
      choice.case === 'ListRef'
        ? {
            ...block,
            Kind: { case: 'ListRef', value: { ...choice.value, OtherAllowed } },
          }
        : updateNoteChoice(block, { OtherAllowed }),
    );
  };

  return (
    <PopupSettings dataComponent="workspace-template-field-popup">
      <IdRow id={block.ID} />
      <CodeInput
        value={block.SlotID ?? ''}
        onChange={(SlotID) => onChange({ ...block, SlotID })}
      />
      {choice && (
        <>
          <ModeSwitch
            label={formatMessage({
              id: 'workspace.templateEdit.selectionMode',
              defaultMessage: 'How many values',
            })}
            offLabel={formatMessage({
              id: 'workspace.templateEdit.selectionOne',
              defaultMessage: 'One option',
            })}
            onLabel={formatMessage({
              id: 'workspace.templateEdit.selectionSeveral',
              defaultMessage: 'Several options',
            })}
            help={formatMessage({
              id: 'workspace.templateEdit.selectionModeHelp',
              defaultMessage:
                'One option — one value is chosen; a new one replaces the previous. Several options — several can be chosen; dictation adds to those already chosen.',
            })}
            on={selection === 'several'}
            onChange={(several) =>
              onChange(setNoteBlockMultiple(block, several))
            }
          />
          <ModeSwitch
            label={formatMessage({
              id: 'workspace.templateEdit.otherMode',
              defaultMessage: 'Values outside the list',
            })}
            offLabel={formatMessage({
              id: 'workspace.templateEdit.otherOff',
              defaultMessage: 'Only from the list',
            })}
            onLabel={formatMessage({
              id: 'workspace.templateEdit.otherOn',
              defaultMessage: 'Own words allowed',
            })}
            help={formatMessage({
              id: 'workspace.templateEdit.otherModeHelp',
              defaultMessage:
                'Only from the list — only a value from the list is recorded; if the clinician said something else, the field stays empty. Own words allowed — if what was said is not in the list, the AI records it in the clinician’s words, and the clinician can type their own. The AI cannot make anything up.',
            })}
            on={choice.value.OtherAllowed}
            onChange={setOther}
          />
        </>
      )}
      {Kind.case === 'Teeth' && (
        <ModeSwitch
          label={formatMessage({
            id: 'workspace.templateEdit.teethMode',
            defaultMessage: 'How many teeth',
          })}
          offLabel={formatMessage({
            id: 'workspace.templateEdit.teethOne',
            defaultMessage: 'One tooth',
          })}
          onLabel={formatMessage({
            id: 'workspace.templateEdit.severalTeeth',
            defaultMessage: 'Several teeth',
          })}
          help={formatMessage({
            id: 'workspace.templateEdit.teethModeHelp',
            defaultMessage:
              'One tooth — one tooth is marked; a new one replaces the previous. Several teeth — several can be marked; dictation adds to those already marked.',
          })}
          on={Kind.value.Multiple !== false}
          onChange={(several) => onChange(setNoteBlockMultiple(block, several))}
        />
      )}
      {Kind.case === 'Number' && (
        <UnitSelect
          value={Kind.value.Unit}
          optional
          onChange={(Unit) =>
            onChange({
              ...block,
              Kind: { case: 'Number', value: { ...Kind.value, Unit } },
            })
          }
        />
      )}
      {Kind.case === 'Table' && (
        <TableSettings block={block} onChange={onChange} />
      )}
      {noteAllowsSetting(block, 'allow_none') && none && (
        <NoneSettings
          value={none}
          onChange={(next) =>
            onChange(
              dropNoneDefault({
                ...block,
                Kind: { ...Kind, value: { ...Kind.value, ...next } },
              } as NoteBlockDef),
            )
          }
        />
      )}
      <RemoveButton onClick={onRemove}>
        <FormattedMessage
          id="workspace.template.deleteField"
          defaultMessage="Delete field"
        />
      </RemoveButton>
    </PopupSettings>
  );
};

// An AI instruction's pop-up: its free-text block's id and code. The
// instruction itself is the text between its brackets.
export const SlotPopupSettings: FC<{
  block: NoteBlockDef;
  onChange: (block: NoteBlockDef) => void;
}> = ({ block, onChange }) => (
  <PopupSettings dataComponent="workspace-template-slot-popup">
    <IdRow id={block.ID} />
    <CodeInput
      value={block.SlotID ?? ''}
      onChange={(SlotID) => onChange({ ...block, SlotID })}
    />
  </PopupSettings>
);

type SectionPopupProps = {
  id: string;
  code: string;
  // What the code does, under it.
  codeHint?: ReactNode;
  // A protocol's section: whether a note needs it and whether AI may reword
  // its wording.
  required?: boolean;
  rewrite?: boolean;
  removeLabel?: ReactNode;
  onRequired?: () => void;
  onRewrite?: () => void;
  onRemove?: () => void;
};

// A section's pop-up, or a field's of a summary or an evaluation: its id
// and code, and in a protocol whether a note needs it and whether AI may
// reword it. Its AI instructions are kept as stored.
export const SectionPopupSettings: FC<SectionPopupProps> = ({
  id,
  code,
  codeHint,
  required,
  rewrite,
  removeLabel,
  onRequired,
  onRewrite,
  onRemove,
}) => (
  <PopupSettings dataComponent="workspace-template-section-popup">
    <IdRow id={id} />
    <div
      className={styles.field}
      data-component="workspace-template-popup-section-code"
    >
      <span className={styles.fieldLabel}>
        <FormattedMessage id="global.code" defaultMessage="Code" />
      </span>
      <code className={cn('p4', styles.idValue)}>{code}</code>
      {codeHint && <span className={cn('p4', styles.hint)}>{codeHint}</span>}
    </div>
    {onRequired && (
      <Checkbox
        className={cn('p4', styles.multiple)}
        checked={!!required}
        testID="workspace-template-required"
        onChange={onRequired}
      >
        <FormattedMessage
          id="workspace.template.required"
          defaultMessage="Required section"
        />
      </Checkbox>
    )}
    {onRewrite && (
      <Checkbox
        className={cn('p4', styles.multiple)}
        checked={!!rewrite}
        testID="workspace-template-rewrite"
        onChange={onRewrite}
      >
        <FormattedMessage
          id="workspace.noteEditor.sectionRewrite"
          defaultMessage="AI may rewrite the wording"
        />
      </Checkbox>
    )}
    {onRemove && <RemoveButton onClick={onRemove}>{removeLabel}</RemoveButton>}
  </PopupSettings>
);
